import React, { useState, useEffect, useRef } from 'react';

interface LazyImageProps {
    src: string;
    alt: string;
    className?: string;
    forceLoading?: boolean;
    smallImageSrc?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({
    src,
    alt,
    className = '',
    forceLoading = false,
    smallImageSrc
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSmallImageLoaded, setIsSmallImageLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [smallImageUrl, setSmallImageUrl] = useState<string>('');
    
    // Use a ref to keep track of the current src for comparison
    const prevSrcRef = useRef<string | null>(null);
    
    // Use a unique key to force re-render of the img element when src changes
    const [imgKey, setImgKey] = useState<number>(0);

    // Annoyingly we can triggers here multiple times with the same `src`
    useEffect(() => {
        // Only reset states and force reload if src actually changed
        if (src !== prevSrcRef.current) {
            // Store current src for future comparison
            prevSrcRef.current = src;
            
            // Reset states
            setIsLoaded(false);
            setHasError(false);
            
            // Increment key to force a new img element
            setImgKey(prevKey => prevKey + 1);
            
            if (src) {
                // Process URL if needed
                let finalSrc = src;
                if (!/^(https?:\/\/|data:)/i.test(finalSrc.trim())) {
                    finalSrc = `https://${finalSrc}`;
                }
                
                setImageUrl(finalSrc);
            }
        }
    }, [src]);

    // Effect for handling the small image URL
    useEffect(() => {
        if (smallImageSrc) {
            // Process URL if needed
            let finalSmallSrc = smallImageSrc;
            if (!/^(https?:\/\/|data:)/i.test(finalSmallSrc.trim())) {
                finalSmallSrc = `https://${finalSmallSrc}`;
            }
            
            setSmallImageUrl(finalSmallSrc);
            
            // Reset small image loaded state when URL changes
            setIsSmallImageLoaded(false);
        } else {
            setSmallImageUrl('');
            setIsSmallImageLoaded(false);
        }
    }, [smallImageSrc]);

    // Handle main image load success
    const handleImageLoad = () => {
        setIsLoaded(true);
    };

    // Handle main image load error
    const handleImageError = () => {
        setHasError(true);
        setIsLoaded(false);
    };

    // Handle small image load success
    const handleSmallImageLoad = () => {
        setIsSmallImageLoaded(true);
    };

    // Handle small image load error
    const handleSmallImageError = () => {
        // If small image fails, we just don't show it
        // but don't set the main hasError state
        setIsSmallImageLoaded(false);
    };

    // Determine if we should show loading state
    const showLoading = forceLoading || 
        (!isLoaded && !hasError && (!smallImageUrl || !isSmallImageLoaded));
    
    // Determine if we should show the small image
    const showSmallImage = smallImageUrl && isSmallImageLoaded && !isLoaded && !hasError;

    console.log("show loading", showLoading, "force loading", forceLoading);

    return (
        <div className="relative w-full h-full overflow-hidden">
            
            {/* Small image - only shown while main image is loading */}
            {smallImageUrl && (
                <img
                    src={smallImageUrl}
                    alt={alt}
                    className={`${className} w-full object-cover absolute inset-0 ${showSmallImage ? 'opacity-100' : 'opacity-0'
                        }`}
                    onLoad={handleSmallImageLoad}
                    onError={handleSmallImageError}
                    // We can use eager loading here since it's likely cached
                    loading="eager"
                />
            )}

            {/* Main image */}
            {imageUrl && (
                <img
                    key={imgKey} // Force re-creation of img element when src changes
                    src={imageUrl}
                    alt={alt}
                    className={`${className} w-full object-cover ${isLoaded && !hasError ? 'opacity-100' : 'opacity-0'
                        }`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    loading="lazy"
                />
            )}

            {/* Error UI */}
            {hasError && (
                <div className="absolute inset-0 bg-gray-100 flex flex-col items-center justify-center text-gray-500 border border-gray-200 rounded z-10">
                    <svg
                        className="w-10 h-10 mb-2"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p className="text-sm font-medium">Image failed to load</p>
                    <p className="text-xs mt-1 max-w-xs text-center text-gray-400">{alt}</p>
                </div>
            )}

            {/* Skeleton loader always shows but controls visibility with opacity */}
            <div
                className={`absolute inset-0 transition-opacity duration-300 ease-in-out ${showLoading ? 'opacity-100' : 'opacity-0'
                    }`}
            >
                <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse">
                    <svg
                        className="w-8 h-8 text-gray-300"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default LazyImage;